/**
 * Hook for fetching and caching user profile picture using React Query
 * This prevents duplicate API calls when multiple components need the profile picture
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, makeAbsolutePhotoUrl } from '../services/api';
import { useKeycloak } from '../contexts/KeycloakContext';
import { logger } from '../services/logger';

// Query key for profile picture - unique per user session
const PROFILE_PICTURE_QUERY_KEY = ['userProfilePicture'];

interface ProfilePictureResponse {
  profile_picture_url: string | null;
}

/**
 * Fetches the user's profile picture URL from the API
 */
const fetchProfilePicture = async (): Promise<string | null> => {
  try {
    const response = await api.get<ProfilePictureResponse>('/user/profile/picture');

    if (response.data && response.data.profile_picture_url) {
      const absoluteUrl = makeAbsolutePhotoUrl(response.data.profile_picture_url);
      if (absoluteUrl) {
        // Add timestamp to bust cache when URL changes
        const urlWithCache = `${absoluteUrl}?t=${Date.now()}`;
        logger.debug('useUserProfilePicture', 'Profile picture fetched', { url: urlWithCache });
        return urlWithCache;
      }
    }
    return null;
  } catch (error) {
    logger.error('useUserProfilePicture', 'Failed to fetch profile picture', error as Error);
    return null;
  }
};

/**
 * Hook to get and cache the user's profile picture
 * Uses React Query for automatic caching and deduplication
 */
export function useUserProfilePicture() {
  const { authenticated } = useKeycloak();
  const queryClient = useQueryClient();

  const { data: profilePicture, isLoading, error } = useQuery({
    queryKey: PROFILE_PICTURE_QUERY_KEY,
    queryFn: fetchProfilePicture,
    enabled: authenticated,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component remounts - use cache
  });

  /**
   * Invalidate the profile picture cache and trigger a refetch
   * Call this after the user updates their profile picture
   */
  const invalidateProfilePicture = () => {
    logger.info('useUserProfilePicture', 'Invalidating profile picture cache');
    queryClient.invalidateQueries({ queryKey: PROFILE_PICTURE_QUERY_KEY });
  };

  return {
    profilePicture: profilePicture ?? null,
    isLoading,
    error,
    invalidateProfilePicture,
  };
}

export { PROFILE_PICTURE_QUERY_KEY };
